import { useEffect, useState, useCallback, useMemo } from "react";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Acquisition, AcquisitionPhase, AcquisitionSource, DealType } from "shared/types/acquisition";
import { BuildingDTO } from "shared/types/building";
import { useUsers } from "shared/contexts/users.context";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { ListBulletsIcon, EnvelopeIcon } from "@phosphor-icons/react";
import { ArrowUpRight } from "assets/icons";
import { AcquisitionsSection, AcquisitionsHighlights } from "./components";
import { useNavigate } from "react-router-dom";
import ButtonNew from "components/buttonNew/buttonNew";
import { EmailImportModal } from "components/emailImport/emailImportModal";
import AddAcquisition from "components/addAcquisition/addAcquisition";
import { FilterBar } from 'shared/components/filterBar';
import type { FilterSection } from 'shared/components/filterBar';

const Acquisitions = () => {
  const [acquisitions, setAcquisitions] = useState<Acquisition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailImportOpen, setIsEmailImportOpen] = useState(false);
  const [isAddAcquisitionOpen, setIsAddAcquisitionOpen] = useState(false);
  const [createAcquisitionPhase, setCreateAcquisitionPhase] = useState<AcquisitionPhase>(AcquisitionPhase.INBOX);
  const [acquisitionToComplete, setAcquisitionToComplete] = useState<Acquisition | null>(null);
  const { session } = useSession();
  const navigate = useNavigate();
  
  // FilterBar states
  const [searchText, setSearchText] = useState('');
  const [phaseFilters, setPhaseFilters] = useState<string[]>([]);
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [buildingFilters, setBuildingFilters] = useState<string[]>([]);
  const [proLabelFilters, setProLabelFilters] = useState<string[]>([]);
  const [conLabelFilters, setConLabelFilters] = useState<string[]>([]);
  const [responsibleFilters, setResponsibleFilters] = useState<string[]>([]);
  const [aangebrachtFilters, setAangebrachtFilters] = useState<string[]>([]);
  const [postcodeFilters, setPostcodeFilters] = useState<string[]>([]);
  const [provinceFilters, setProvinceFilters] = useState<string[]>([]);
  const [dealTypeFilters, setDealTypeFilters] = useState<string[]>([]);

  const { users } = useUsers();

  const fetchAcquisitions = useCallback(async () => {
    setIsLoading(true);
    try {
      let res = await fetchWithAuth(
        `${API_URL}/acquisitions`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session,
      );
      const acquisitions = await res.json();
      setAcquisitions(acquisitions);
    } catch (e) {
      console.error(e);
      toast.error("Er is een fout opgetreden bij het ophalen van acquisities.");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies - session is captured but doesn't trigger recreate

  const handleAcquisitionClick = useCallback((acquisition: Acquisition) => {
    navigate(`/acquisitions/${acquisition.id}/detail`);
  }, [navigate]);

  const handleCreateAcquisitionWithPhase = useCallback((phase: AcquisitionPhase) => {
    setCreateAcquisitionPhase(phase);
    setAcquisitionToComplete(null);
    setIsAddAcquisitionOpen(true);
  }, []);

  const handleCompleteAcquisition = useCallback((acquisition: Acquisition) => {
    setAcquisitionToComplete(acquisition);
    setCreateAcquisitionPhase(acquisition.acquisitionPhase || AcquisitionPhase.INBOX);
    setIsAddAcquisitionOpen(true);
  }, []);

  useEffect(() => {
    fetchAcquisitions();
  }, [fetchAcquisitions]);

  // FilterBar available options
  const availablePhases = useMemo(() => [
    { key: AcquisitionPhase.INBOX, label: 'Inbox' },
    { key: AcquisitionPhase.ADD_DATA, label: 'Opgepikt' },
    { key: AcquisitionPhase.ACQUISITION_MEETING_AGENDA, label: 'Agenda ACQ meeting' },
    { key: AcquisitionPhase.RVB_AGENDA, label: 'Agenda RVB' },
    { key: AcquisitionPhase.DEAL, label: 'Deal!' },
    { key: AcquisitionPhase.REJECTED, label: 'Afgewezen' },
  ], []);

  const availableSources = useMemo(() => [
    { key: AcquisitionSource.IMMO_WEBSITE, label: 'Immo website' },
    { key: AcquisitionSource.NETWORK, label: 'Netwerk' },
    { key: AcquisitionSource.PUBLIC_SALE, label: 'Openbare verkoop' },
    { key: AcquisitionSource.YALLY_WEBSITE, label: 'Yally website' },
  ], []);

  const buildingLabel = useCallback((b: BuildingDTO): string => {
    if (b.googlePlaceLongAddress) return b.googlePlaceLongAddress;
    const street = [b.street, b.housenr].filter(Boolean).join(' ').trim();
    const parts = [street, b.city].filter(Boolean);
    return parts.length ? parts.join(', ') : (b.id || 'Onbekend gebouw');
  }, []);

  // Buildings — derived from loaded acquisitions so only relevant options appear
  const availableBuildings = useMemo(() => {
    const seen = new Map<string, string>();
    acquisitions.forEach((a) => {
      a.buildings?.forEach((b) => {
        const id = b.id || b.resourceId;
        if (id && !seen.has(id)) {
          seen.set(id, buildingLabel(b));
        }
      });
    });
    return Array.from(seen.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((x, y) => x.label.localeCompare(y.label, 'nl'));
  }, [acquisitions, buildingLabel]);

  // Pro/Con labels — derived from loaded acquisitions so only used labels appear
  const availableProLabels = useMemo(() => {
    const seen = new Set<string>();
    acquisitions.forEach((a) => a.acquisitionProReasons?.forEach((l) => seen.add(l)));
    return Array.from(seen)
      .sort((x, y) => x.localeCompare(y, 'nl'))
      .map((l) => ({ key: l, label: l }));
  }, [acquisitions]);

  const availableConLabels = useMemo(() => {
    const seen = new Set<string>();
    acquisitions.forEach((a) => a.acquisitionConReasons?.forEach((l) => seen.add(l)));
    return Array.from(seen)
      .sort((x, y) => x.localeCompare(y, 'nl'))
      .map((l) => ({ key: l, label: l }));
  }, [acquisitions]);

  // Resolve a user id to a friendly name; falls back to id.
  const resolveUserName = useCallback(
    (id: string): string => {
      if (id === 'unassigned') return 'Niet toegewezen';
      const u = users.find((x: any) => x.id === id);
      if (!u) return id;
      return (
        `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
        u.email ||
        id
      );
    },
    [users],
  );

  // Filter options derived from loaded acquisitions so only relevant users appear.
  const buildUserOptions = useCallback(
    (extractor: (a: Acquisition) => string | null | undefined) => {
      const seen = new Set<string>();
      acquisitions.forEach((a) => {
        const id = extractor(a) || null;
        seen.add(id || 'unassigned');
      });
      return Array.from(seen)
        .map((id) => ({ key: id, label: resolveUserName(id) }))
        .sort((x, y) => x.label.localeCompare(y.label, 'nl'));
    },
    [acquisitions, resolveUserName],
  );

  const availableResponsibles = useMemo(
    () => buildUserOptions((a) => a.yallyResponsibleUserId),
    [buildUserOptions],
  );

  const availableAangebracht = useMemo(
    () =>
      buildUserOptions(
        (a) => a.aangebrachtDoor || a.yallyResponsibleUserId,
      ),
    [buildUserOptions],
  );

  // Extract a 4-digit postcode from a building. Tries (in order):
  // 1) building.id (we encode it as <zip>-<street>-<housenr> server-side)
  // 2) explicit zip field
  // 3) regex on googlePlaceLongAddress
  const extractBuildingPostcodes = useCallback((b: BuildingDTO): string[] => {
    const codes = new Set<string>();
    const fromId = b.id?.match(/^(\d{4})/);
    if (fromId) codes.add(fromId[1]);
    const explicit = b.zip?.trim();
    if (explicit && /^\d{4}$/.test(explicit)) codes.add(explicit);
    const long = b.googlePlaceLongAddress;
    if (long) {
      const m = long.match(/\b\d{4}\b/);
      if (m) codes.add(m[0]);
    }
    return Array.from(codes);
  }, []);

  // Map a Belgian 4-digit postcode to its provincie (NL).
  // Same BPost ranges as the backend statistics service.
  const postcodeToProvince = useCallback((zip: string): string | null => {
    if (!/^\d{4}$/.test(zip)) return null;
    const n = Number(zip);
    if (n >= 1000 && n <= 1299) return 'Brussel';
    if (n >= 1300 && n <= 1499) return 'Waals-Brabant';
    if (n >= 1500 && n <= 1999) return 'Vlaams-Brabant';
    if (n >= 2000 && n <= 2999) return 'Antwerpen';
    if (n >= 3000 && n <= 3499) return 'Vlaams-Brabant';
    if (n >= 3500 && n <= 3999) return 'Limburg';
    if (n >= 4000 && n <= 4999) return 'Luik';
    if (n >= 5000 && n <= 5999) return 'Namen';
    if (n >= 6000 && n <= 6599) return 'Henegouwen';
    if (n >= 6600 && n <= 6999) return 'Luxemburg';
    if (n >= 7000 && n <= 7999) return 'Henegouwen';
    if (n >= 8000 && n <= 8999) return 'West-Vlaanderen';
    if (n >= 9000 && n <= 9999) return 'Oost-Vlaanderen';
    return null;
  }, []);

  const extractBuildingProvinces = useCallback(
    (b: BuildingDTO): string[] => {
      const provs = new Set<string>();
      extractBuildingPostcodes(b).forEach((zip) => {
        const p = postcodeToProvince(zip);
        if (p) provs.add(p);
      });
      return Array.from(provs);
    },
    [extractBuildingPostcodes, postcodeToProvince],
  );

  // Postcodes that actually appear in loaded acquisitions, sorted ascending.
  const availablePostcodes = useMemo(() => {
    const seen = new Set<string>();
    acquisitions.forEach((a) =>
      a.buildings?.forEach((b) =>
        extractBuildingPostcodes(b).forEach((c) => seen.add(c)),
      ),
    );
    return Array.from(seen)
      .sort((x, y) => x.localeCompare(y))
      .map((p) => ({ key: p, label: p }));
  }, [acquisitions, extractBuildingPostcodes]);

  // Provincies derived from postcodes, sorted alphabetically (NL).
  const availableProvinces = useMemo(() => {
    const seen = new Set<string>();
    acquisitions.forEach((a) =>
      a.buildings?.forEach((b) =>
        extractBuildingProvinces(b).forEach((p) => seen.add(p)),
      ),
    );
    return Array.from(seen)
      .sort((x, y) => x.localeCompare(y, 'nl'))
      .map((p) => ({ key: p, label: p }));
  }, [acquisitions, extractBuildingProvinces]);

  // Deal types — fixed enum, but only show the ones that actually appear so
  // the dropdown stays clean (still always include the enum for completeness).
  const availableDealTypes = useMemo(
    () => [
      { key: DealType.ASSET_DEAL, label: 'Asset deal' },
      { key: DealType.SHARE_DEAL, label: 'Share deal' },
      { key: DealType.UNKNOWN, label: 'Onbekend' },
    ],
    [],
  );

  // FilterBar update function
  const updateFilters = useCallback((key: string, values: string[]) => {
    switch (key) {
      case 'phase':
        setPhaseFilters(values);
        break;
      case 'source':
        setSourceFilters(values);
        break;
      case 'building':
        setBuildingFilters(values);
        break;
      case 'proLabel':
        setProLabelFilters(values);
        break;
      case 'conLabel':
        setConLabelFilters(values);
        break;
      case 'responsible':
        setResponsibleFilters(values);
        break;
      case 'aangebracht':
        setAangebrachtFilters(values);
        break;
      case 'postcode':
        setPostcodeFilters(values);
        break;
      case 'province':
        setProvinceFilters(values);
        break;
      case 'dealType':
        setDealTypeFilters(values);
        break;
    }
  }, []);

  // FilterBar sections configuration
  const filterSections: FilterSection[] = useMemo(() => [
    {
      key: 'phase',
      label: 'Status',
      options: availablePhases,
      selectedValues: phaseFilters,
      onUpdate: (values) => updateFilters('phase', values),
      color: 'moss' as const,
    },
    {
      key: 'building',
      label: 'Gebouw',
      options: availableBuildings,
      selectedValues: buildingFilters,
      onUpdate: (values) => updateFilters('building', values),
      color: 'neutral' as const,
    },
    {
      key: 'proLabel',
      label: 'Pro labels',
      options: availableProLabels,
      selectedValues: proLabelFilters,
      onUpdate: (values) => updateFilters('proLabel', values),
      color: 'moss' as const,
    },
    {
      key: 'conLabel',
      label: 'Con labels',
      options: availableConLabels,
      selectedValues: conLabelFilters,
      onUpdate: (values) => updateFilters('conLabel', values),
      color: 'brick' as const,
    },
    {
      key: 'responsible',
      label: 'Verantwoordelijke',
      options: availableResponsibles,
      selectedValues: responsibleFilters,
      onUpdate: (values) => updateFilters('responsible', values),
      color: 'neutral' as const,
    },
    {
      key: 'aangebracht',
      label: 'Aangebracht door',
      options: availableAangebracht,
      selectedValues: aangebrachtFilters,
      onUpdate: (values) => updateFilters('aangebracht', values),
      color: 'moss' as const,
    },
    {
      key: 'province',
      label: 'Provincie',
      options: availableProvinces,
      selectedValues: provinceFilters,
      onUpdate: (values) => updateFilters('province', values),
      color: 'moss' as const,
    },
    {
      key: 'postcode',
      label: 'Postcode',
      options: availablePostcodes,
      selectedValues: postcodeFilters,
      onUpdate: (values) => updateFilters('postcode', values),
      color: 'neutral' as const,
    },
    {
      key: 'dealType',
      label: 'Type deal',
      options: availableDealTypes,
      selectedValues: dealTypeFilters,
      onUpdate: (values) => updateFilters('dealType', values),
      color: 'brick' as const,
    },
    {
      key: 'source',
      label: 'Bron',
      options: availableSources,
      selectedValues: sourceFilters,
      onUpdate: (values) => updateFilters('source', values),
      color: 'yellow' as const,
    },
  ], [
    availablePhases,
    availableSources,
    availableBuildings,
    availableProLabels,
    availableConLabels,
    availableResponsibles,
    availableAangebracht,
    availablePostcodes,
    availableProvinces,
    availableDealTypes,
    phaseFilters,
    sourceFilters,
    buildingFilters,
    proLabelFilters,
    conLabelFilters,
    responsibleFilters,
    aangebrachtFilters,
    postcodeFilters,
    provinceFilters,
    dealTypeFilters,
    updateFilters,
  ]);

  // FilterBar filter chips
  const filterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      category?: string;
      color?: 'moss' | 'brick' | 'yellow' | 'neutral';
      onRemove: () => void;
    }> = [];
    
    // Phase filter chips
    phaseFilters.forEach(phase => {
      const phaseOption = availablePhases.find(p => p.key === phase);
      if (phaseOption) {
        chips.push({
          key: `phase-${phase}`,
          label: phaseOption.label,
          category: 'Status',
          color: 'moss' as const,
          onRemove: () => setPhaseFilters(prev => prev.filter(p => p !== phase))
        });
      }
    });

    // Building filter chips
    buildingFilters.forEach(buildingId => {
      const buildingOption = availableBuildings.find(b => b.key === buildingId);
      if (buildingOption) {
        chips.push({
          key: `building-${buildingId}`,
          label: buildingOption.label,
          category: 'Gebouw',
          color: 'neutral' as const,
          onRemove: () => setBuildingFilters(prev => prev.filter(b => b !== buildingId))
        });
      }
    });

    // Pro label filter chips
    proLabelFilters.forEach(label => {
      chips.push({
        key: `proLabel-${label}`,
        label,
        category: 'Pro',
        color: 'moss' as const,
        onRemove: () => setProLabelFilters(prev => prev.filter(l => l !== label))
      });
    });

    // Con label filter chips
    conLabelFilters.forEach(label => {
      chips.push({
        key: `conLabel-${label}`,
        label,
        category: 'Con',
        color: 'brick' as const,
        onRemove: () => setConLabelFilters(prev => prev.filter(l => l !== label))
      });
    });

    // Source filter chips
    sourceFilters.forEach(source => {
      const sourceOption = availableSources.find(s => s.key === source);
      if (sourceOption) {
        chips.push({
          key: `source-${source}`,
          label: sourceOption.label,
          category: 'Bron',
          color: 'yellow' as const,
          onRemove: () => setSourceFilters(prev => prev.filter(s => s !== source))
        });
      }
    });

    // Verantwoordelijke chips
    responsibleFilters.forEach(userId => {
      chips.push({
        key: `responsible-${userId}`,
        label: resolveUserName(userId),
        category: 'Verantwoordelijke',
        color: 'neutral' as const,
        onRemove: () =>
          setResponsibleFilters(prev => prev.filter(u => u !== userId)),
      });
    });

    // Aangebracht door chips
    aangebrachtFilters.forEach(userId => {
      chips.push({
        key: `aangebracht-${userId}`,
        label: resolveUserName(userId),
        category: 'Aangebracht door',
        color: 'moss' as const,
        onRemove: () =>
          setAangebrachtFilters(prev => prev.filter(u => u !== userId)),
      });
    });

    // Provincie chips
    provinceFilters.forEach(prov => {
      chips.push({
        key: `province-${prov}`,
        label: prov,
        category: 'Provincie',
        color: 'moss' as const,
        onRemove: () =>
          setProvinceFilters(prev => prev.filter(p => p !== prov)),
      });
    });

    // Postcode chips
    postcodeFilters.forEach(zip => {
      chips.push({
        key: `postcode-${zip}`,
        label: zip,
        category: 'Postcode',
        color: 'neutral' as const,
        onRemove: () =>
          setPostcodeFilters(prev => prev.filter(z => z !== zip)),
      });
    });

    // Deal type chips
    dealTypeFilters.forEach(dt => {
      const opt = availableDealTypes.find(o => o.key === dt);
      chips.push({
        key: `dealType-${dt}`,
        label: opt?.label || dt,
        category: 'Type deal',
        color: 'brick' as const,
        onRemove: () =>
          setDealTypeFilters(prev => prev.filter(x => x !== dt)),
      });
    });

    return chips;
  }, [
    phaseFilters,
    sourceFilters,
    buildingFilters,
    proLabelFilters,
    conLabelFilters,
    responsibleFilters,
    aangebrachtFilters,
    postcodeFilters,
    provinceFilters,
    dealTypeFilters,
    availablePhases,
    availableSources,
    availableBuildings,
    availableDealTypes,
    resolveUserName,
  ]);

  // Apply filters to acquisitions
  const getFilteredAcquisitions = useCallback(() => {
    if (!acquisitions?.length) return [];
    
    return acquisitions.filter((acquisition) => {
      // Phase filter
      if (phaseFilters.length > 0) {
        if (!acquisition.acquisitionPhase || !phaseFilters.includes(acquisition.acquisitionPhase)) {
          return false;
        }
      }

      // Source filter
      if (sourceFilters.length > 0) {
        if (!acquisition.acquisitionSource || !sourceFilters.includes(acquisition.acquisitionSource)) {
          return false;
        }
      }

      // Building filter — keep when at least one underlying building matches
      if (buildingFilters.length > 0) {
        const hasMatch = acquisition.buildings?.some((b) => {
          const id = b.id || b.resourceId;
          return id ? buildingFilters.includes(id) : false;
        });
        if (!hasMatch) return false;
      }

      // Pro label filter — keep when at least one selected pro label is present
      if (proLabelFilters.length > 0) {
        const hasMatch = acquisition.acquisitionProReasons?.some((l) =>
          proLabelFilters.includes(l)
        );
        if (!hasMatch) return false;
      }

      // Con label filter — keep when at least one selected con label is present
      if (conLabelFilters.length > 0) {
        const hasMatch = acquisition.acquisitionConReasons?.some((l) =>
          conLabelFilters.includes(l)
        );
        if (!hasMatch) return false;
      }

      // Responsible filter
      if (responsibleFilters.length > 0) {
        const respKey = acquisition.yallyResponsibleUserId || 'unassigned';
        if (!responsibleFilters.includes(respKey)) return false;
      }

      // Aangebracht door filter (falls back to responsible for legacy rows)
      if (aangebrachtFilters.length > 0) {
        const aangKey =
          acquisition.aangebrachtDoor ||
          acquisition.yallyResponsibleUserId ||
          'unassigned';
        if (!aangebrachtFilters.includes(aangKey)) return false;
      }

      // Postcode filter — keep when at least one underlying building's
      // postcode matches a selected one.
      if (postcodeFilters.length > 0) {
        const hasMatch = acquisition.buildings?.some((b) =>
          extractBuildingPostcodes(b).some((c) =>
            postcodeFilters.includes(c),
          ),
        );
        if (!hasMatch) return false;
      }

      // Provincie filter — derived from postcodes; keep when any building
      // resolves to a selected provincie.
      if (provinceFilters.length > 0) {
        const hasMatch = acquisition.buildings?.some((b) =>
          extractBuildingProvinces(b).some((p) =>
            provinceFilters.includes(p),
          ),
        );
        if (!hasMatch) return false;
      }

      // Deal type filter
      if (dealTypeFilters.length > 0) {
        if (
          !acquisition.dealType ||
          !dealTypeFilters.includes(acquisition.dealType)
        ) {
          return false;
        }
      }

      // Search filter
      const term = searchText.trim().toLowerCase();
      if (term) {
        const matches = (
          acquisition.name?.toLowerCase().includes(term) ||
          acquisition.description?.toLowerCase().includes(term) ||
          acquisition.emailSubject?.toLowerCase().includes(term) ||
          acquisition.buildings?.some(b => {
            const codes = extractBuildingPostcodes(b);
            const provs = extractBuildingProvinces(b);
            return (
              b.street?.toLowerCase().includes(term) ||
              b.city?.toLowerCase().includes(term) ||
              b.googlePlaceLongAddress?.toLowerCase().includes(term) ||
              codes.some(c => c.includes(term)) ||
              provs.some(p => p.toLowerCase().includes(term))
            );
          })
        );

        if (!matches) return false;
      }

      return true;
    });
  }, [
    acquisitions,
    phaseFilters,
    sourceFilters,
    buildingFilters,
    proLabelFilters,
    conLabelFilters,
    responsibleFilters,
    aangebrachtFilters,
    postcodeFilters,
    provinceFilters,
    dealTypeFilters,
    extractBuildingPostcodes,
    extractBuildingProvinces,
    searchText,
  ]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Acquisities &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>
      
      <div className="mb-10 flex flex-col gap-8">
        <header className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-lg font-medium">
              <ListBulletsIcon weight="duotone" size={20} />
              <p>Acquisities</p>
            </div>
            <div className="flex items-center gap-2">
              <ButtonNew
                onClick={() => setIsEmailImportOpen(true)}
                icon={<EnvelopeIcon />}
                label="Import acquisitions@yally.be"
                variant="primaryMoss"
              />
              <a
                className="flex items-center gap-1 rounded-full border border-neutral-primary/20 px-3 py-1.5 text-sm font-medium text-moss-primary hover:bg-moss-primary/5 transition-colors"
                href="http://immoweb.be"
                target="_blank"
              >
                Immoweb
                <ArrowUpRight className="h-[18px] w-[18px]" />
              </a>
              <a
                className="flex items-center gap-1 rounded-full border border-neutral-primary/20 px-3 py-1.5 text-sm font-medium text-moss-primary hover:bg-moss-primary/5 transition-colors"
                href="http://biddit.be"
                target="_blank"
              >
                Biddit
                <ArrowUpRight className="h-[18px] w-[18px]" />
              </a>
            </div>
          </div>
        </header>

        {/* Highlights strip — board-level KPIs computed for current year + 30d */}
        <AcquisitionsHighlights />

        {/* Kanban Board Section with all phases including Inbox */}
        <AcquisitionsSection
          acquisitions={getFilteredAcquisitions()}
          loading={isLoading}
          onUpdate={fetchAcquisitions}
          onAcquisitionClick={handleAcquisitionClick}
          onAddClick={handleCreateAcquisitionWithPhase}
          onCompleteAcquisition={handleCompleteAcquisition}
        />

        {/* Email Import Modal */}
        <EmailImportModal
          isOpen={isEmailImportOpen}
          onOpenChange={setIsEmailImportOpen}
          onEmailsImported={fetchAcquisitions}
        />

        {/* Add Acquisition Modal */}
        <AddAcquisition
          isOpen={isAddAcquisitionOpen}
          onOpenChange={setIsAddAcquisitionOpen}
          onClose={() => {
            setIsAddAcquisitionOpen(false);
            setAcquisitionToComplete(null);
            fetchAcquisitions();
          }}
          initialPhase={createAcquisitionPhase}
          existingAcquisition={acquisitionToComplete}
        />
      </div>

      {/* FilterBar at parent level */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder="Zoek op naam, gebouw, postcode of provincie..."
        filterChips={filterChips}
        filterSections={filterSections}
        showWhenEmpty={true}
        showSpacer={false}
      />
    </>
  );
};

export default Acquisitions;
